import { ActionsSlot, ContentSlot, IconSlot, TitleSlot } from '@magicschool/spellbook/components/Alert/slots';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { StatusIcon, type StatusIconProps } from '@magicschool/spellbook/components/StatusIcon';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentProps, FC } from 'react';

export type AlertProps = ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    /** Callback function when the alert is dismissed. */
    onDismiss?: () => void;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** The accessibility label for the dismiss button. */
      dismissButtonLabel?: string;
    };
  };

const alertVariants = cva(['@container', 'relative', 'w-full', 'rounded-sm', 'flex', 'gap-2'], {
  variants: {
    /** The status of the alert. This determines the background color and icon. */
    status: {
      primary: 'bg-primary-muted-background border-l-primary-stroke',
      neutral: 'bg-default-muted-background border-l-default-stroke',
      success: 'bg-success-muted-background border-l-success-stroke',
      info: 'bg-info-muted-background border-l-info-stroke',
      warning: 'bg-warning-muted-background border-l-warning-stroke',
      error: 'bg-error-muted-background border-l-error-stroke',
    },
    /** The visual appearance of the alert. */
    variant: {
      inline: 'items-center px-2 py-1.5',
      expanded: 'items-start border-l-2 rounded-l-none p-2',
      banner: 'rounded-none border-b border-border-default py-3 px-2.5 items-center',
    },
  },
});

const iconVariants = cva('size-6 flex items-center justify-center', {
  variants: {
    variant: {
      inline: 'self-start',
      expanded: '',
    },
  },
});

const containerVariants = cva('grow flex flex-col items-end gap-3', {
  variants: {
    variant: {
      inline: 'items-end @lg:items-center @lg:flex-row',
      expanded: '',
    },
  },
});

const titleContentVariants = cva('flex flex-col gap-1 w-full', {
  variants: {
    variant: {
      inline: '@lg:flex-row @lg:gap-2',
      expanded: 'pt-0.5',
    },
    hasActionsOrDismiss: {
      true: '',
      false: 'pe-1',
    },
  },
});

const AlertBase: FC<AlertProps> = ({
  children,
  className,
  onDismiss,
  role: roleOverride,
  text = {},
  status = 'primary',
  variant = 'inline',
  ...props
}) => {
  const { slots } = useSlots(children, {
    icon: { Component: IconSlot, props: { status } },
    title: { Component: TitleSlot, props: { className: 'leading-[1.429]', status } },
    content: { Component: ContentSlot, props: { className: 'leading-[1.429]' } },
    actions: { Component: ActionsSlot },
  });
  const childVariants = { variant: variant === 'banner' ? 'inline' : variant };

  const { dismissButtonLabel = 'Close' } = text;
  const role = roleOverride ?? (status === 'error' ? 'alert' : 'status');
  const isLiveRegion = role === 'alert' || role === 'status';

  return (
    <div
      data-spellbook-name="alert"
      role={role}
      // Any aria-* attribute makes browsers ignore role="presentation".
      aria-atomic={isLiveRegion || undefined}
      className={cn(alertVariants({ status, variant }), className)}
      {...props}
    >
      <div className={cn(iconVariants(childVariants))}>
        {slots.icon || (
          <StatusIcon data-spellbook-name="alert-icon" status={(status === 'neutral' ? 'primary' : status) as StatusIconProps['status']} />
        )}
      </div>
      <div className={cn(containerVariants(childVariants))}>
        <div className={cn(titleContentVariants({ ...childVariants, hasActionsOrDismiss: Boolean(slots.actions || onDismiss) }))}>
          {slots.title}
          {slots.content}
        </div>
        {slots.actions}
      </div>
      {onDismiss ? (
        <IconButton
          // No self-align override: inherits the root row's `items-center` (inline) so the dismiss
          // button stays centered on the alert as a whole, even when title/content wraps to multiple
          // lines (the icon stays `self-start` deliberately, pinned to the title's line).
          data-spellbook-name="alert-dismiss"
          name="x"
          onClick={onDismiss}
          size="sm"
          shape="circle"
          color="neutral"
          variant="ghost"
          aria-label={dismissButtonLabel}
        />
      ) : null}
    </div>
  );
};

/**
 * Persistent notification for important messages that require user attention. Announced to screen readers via an ARIA live region whose urgency follows `status`.
 *
 * ### When to use
 * - For persistent messages that remain visible until the user takes action or dismisses
 * - For form validation summaries at the top of a form
 * - For page-level warnings or errors that affect the entire view
 * - For informational banners about limits, upgrades, or system status
 * - For success confirmations that should remain visible
 *
 * ### When NOT to use
 * - For transient feedback after actions: use `Toast` instead — it auto-dismisses
 * - For inline field validation: display error text directly below the field instead
 * - For blocking confirmations requiring a decision: use `Modal` instead
 * - For loading states: use `Spinner` or `Skeleton` instead
 *
 * ### API
 * - `status`: Semantic color: `primary` (default), `neutral`, `success`, `info`, `warning`, `error`
 * - `variant`: Layout style: `inline` (default, compact), `expanded` (left border accent, more space), `banner` (full-width, no rounding)
 * - `onDismiss`: Callback when alert is dismissed. When provided, shows a close button.
 * - `text.dismissButtonLabel`: Accessible label for dismiss button. Default: "Close"
 *
 * ### Composition (slots)
 * - `<Alert.Icon>`: Custom icon. Renders `StatusIcon` by default based on status. Only include when overriding the default icon.
 * - `<Alert.Title>`: Alert heading (required). Renders as `Text variant="label-1"`. Always include for scannability.
 * - `<Alert.Content>`: Supporting text. Renders as `Text`. Include when additional context is needed beyond the title.
 * - `<Alert.Actions>`: Action buttons. Use `<Button size="sm" variant="ghost">` for actions. Only include when alert requires user action.
 *
 * ### Visual states
 * - **Status colors**: Each status has distinct background and accent colors to convey severity
 * - **Dismissible**: When `onDismiss` is provided, a close button appears in the corner
 * - **Expanded**: Left border accent with more vertical padding for longer content
 * - **Banner**: Full-width with bottom border, no corner rounding — use for page-top announcements
 *
 * ### Accessibility
 * - Only `error` renders `role="alert"` and interrupts the screen reader. Every other status renders `role="status"` and waits its turn, with `aria-atomic="true"` so title and content are read as one message
 * - `warning` is polite on purpose: `role="alert"` announces on render, so an assertive standing banner would interrupt on every page load
 * - Override urgency with `role`: `role="status"` quiets an error, `role="alert"` escalates any other status
 * - Opt out of announcement with `role="presentation"`, e.g. for banners inside a container that owns its own live region. Not `aria-live="off"`, which also removes the alert from any ancestor region
 * - Pass `role` or `aria-live`, never both — they imply each other and a conflicting pair is resolved inconsistently
 * - A polite alert announces when its text changes, not when it first mounts. For feedback on something the user just did, use `Toast`
 * - Status is conveyed visually through color and icon; screen readers get only the text, so it must stand on its own
 * - Dismiss button has configurable `aria-label` via `text.dismissButtonLabel`
 * - Keep titles concise for quick scanning; use Content slot for details
 *
 * ### Additional notes
 * - For long titles that need to wrap, add `className="whitespace-normal"` to `<Alert.Title>`
 * - Unlike `Toast`, Alert does not auto-dismiss — it persists until explicitly dismissed or the user navigates away
 */
export const Alert = Object.assign(AlertBase, {
  Icon: IconSlot,
  Title: TitleSlot,
  Content: ContentSlot,
  Actions: ActionsSlot,
});
